<script setup lang="ts">
import { useVisualViewportBounds } from '@/composables/useVisualViewportBounds';
import NativeDialog from '@/core/dialogsystem/NativeDialog.vue';
import IconButton from '@/core/elements/IconButton.vue';
import type { TPositionablePopoverDrawerCloseReason } from '@/core/dialogs/positionablePopover.utils';
import { EIcon } from 'common/enum/misc.enum';
import { nextTick, onBeforeUnmount, ref, useTemplateRef } from 'vue';

const props = defineProps<{
  accessibleLabel?: string;
}>();

const emit = defineEmits<{
  close: [];
  'request-close': [reason: TPositionablePopoverDrawerCloseReason];
}>();

const dialogRef = useTemplateRef<InstanceType<typeof NativeDialog>>('dialogRef');
const panelVisible = ref(false);
const { fixedViewportStyle } = useVisualViewportBounds();
const closeWaiters = new Set<() => void>();

const show = async (): Promise<void> => {
  panelVisible.value = true;
  await nextTick();
  if (!dialogRef.value?.element?.open) dialogRef.value?.showModal();
  await nextTick();
};

const hide = (): Promise<void> => {
  panelVisible.value = false;
  if (!dialogRef.value?.element?.open) {
    return Promise.resolve();
  }
  return new Promise((resolve) => closeWaiters.add(resolve));
};

const resolveCloseWaiters = (): void => {
  closeWaiters.forEach((resolve) => resolve());
  closeWaiters.clear();
};

const closeAfterTransition = (): void => {
  dialogRef.value?.close();
};

const handleDialogClose = (): void => {
  panelVisible.value = false;
  resolveCloseWaiters();
  emit('close');
};

onBeforeUnmount(resolveCloseWaiters);

defineExpose({ hide, show });
</script>

<template>
  <Teleport to="body">
    <NativeDialog
      ref="dialogRef"
      class="fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none overflow-visible rounded-none border-0 bg-transparent p-0 outline-none"
      :aria-label="props.accessibleLabel"
      :close-on-backdrop-click="false"
      :close-on-cancel="false"
      @backdrop-click="emit('request-close', 'backdrop')"
      @cancel="emit('request-close', 'cancel')"
      @close="handleDialogClose"
    >
      <Transition
        appear
        enter-active-class="motion-safe:transition-[transform,opacity] motion-safe:duration-300 motion-safe:ease-entrance"
        enter-from-class="motion-safe:translate-y-full motion-safe:opacity-0"
        leave-active-class="motion-safe:transition-[transform,opacity] motion-safe:duration-300 motion-safe:ease-entrance"
        leave-to-class="motion-safe:translate-y-full motion-safe:opacity-0"
        @after-leave="closeAfterTransition"
      >
        <div v-if="panelVisible" class="pointer-events-none fixed" :style="fixedViewportStyle">
          <section
            class="pointer-events-auto absolute inset-x-0 bottom-0 flex max-h-mobile-drawer flex-col overflow-hidden rounded-t-dialog bg-surface-primary pb-mobile-safe-bottom-lg pt-md shadow-popover"
          >
            <header class="flex shrink-0 items-center justify-between gap-md px-lg">
              <h2 class="min-w-0 flex-1 truncate text-heading-semibold text-primary">
                {{ props.accessibleLabel }}
              </h2>
              <IconButton
                class="shrink-0"
                :aria-label="$t('general.close')"
                :icon-name="EIcon.X"
                size="button"
                theme="text"
                @click="emit('request-close', 'button')"
              />
            </header>

            <slot name="above-content"></slot>
            <slot></slot>
          </section>
        </div>
      </Transition>
    </NativeDialog>
  </Teleport>
</template>
